import { useState } from 'react'
import { Users, ListChecks, Trophy } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTriviasAnalyticsDifare, useFasesAnalyticsDifare } from '../hooks/useAnalyticsDifare'
import { KpiCard, INDIGO, VERDE, AZUL } from './KpiCard'
import { nf, fechaCorta } from '../format'

export function TriviasTab() {
  const [fase, setFase] = useState<number>(1)
  const { data, isLoading, error } = useTriviasAnalyticsDifare(fase)
  const { data: fases } = useFasesAnalyticsDifare()

  const selector = (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">Fase</span>
      <Select value={String(fase)} onValueChange={(v) => setFase(Number(v))}>
        <SelectTrigger className="w-[240px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(fases ?? [{ numero: 1, nombre: 'Fase 1', fecha_inicio: '', fecha_fin: '' }]).map((f) => (
            <SelectItem key={f.numero} value={String(f.numero)}>
              {f.nombre}
              {f.fecha_inicio ? ` (${fechaCorta(f.fecha_inicio)}–${fechaCorta(f.fecha_fin)})` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )

  if (isLoading) {
    return (
      <div className="space-y-5">
        {selector}
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="space-y-5">
        {selector}
        <p className="text-destructive text-sm text-center max-w-md mx-auto py-12">
          No se pudo cargar el reporte de trivias. Verifique la configuración e intente de nuevo.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {selector}

      {/* KPIs de la fase */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard
          icon={Users}
          label="Participantes"
          value={nf(data.kpis.participantes)}
          sub="Con ≥1 trivia en la fase"
          color={AZUL}
        />
        <KpiCard
          icon={ListChecks}
          label="Completaron"
          value={nf(data.kpis.completaron)}
          sub="Trivia completa de la fase"
          color={VERDE}
        />
        <KpiCard
          icon={Trophy}
          label="Puntaje promedio"
          value={data.kpis.puntaje_promedio.toFixed(1)}
          sub="Entre participantes de la fase"
          color={INDIGO}
        />
      </div>

      {/* Preguntas de la fase */}
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Preguntas de la fase</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]">
                  <TableHead>Producto</TableHead>
                  <TableHead>Pregunta</TableHead>
                  <TableHead>Respuesta correcta</TableHead>
                  <TableHead className="text-right">% aciertos</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.preguntas.length > 0 ? (
                  data.preguntas.map((p, i) => (
                    <TableRow key={`${p.producto}-${i}`}>
                      <TableCell className="font-medium whitespace-nowrap">{p.producto}</TableCell>
                      <TableCell className="max-w-md">{p.pregunta}</TableCell>
                      <TableCell>{p.respuesta_correcta || '—'}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {p.pct_aciertos.toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-10">
                      No hay preguntas/datos para esta fase todavía.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
